<template>
    <div :class="[$style.videoCard, { [$style.playing]: isPlaying }]">
        <!-- 썸네일 / 플레이어 영역 -->
        <div :class="$style.thumbnailContainer" @click="handleThumbnailClick">
            <div v-if="!isPlaying" :class="$style.thumbnail">
                <img :src="`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`" :alt="video.title"
                    :class="$style.thumbnailImg" loading="lazy" />
                <div :class="$style.playOverlay">
                    <div :class="$style.playIcon">▶</div>
                </div>
            </div>
            <div v-else :class="$style.iframeWrapper">
                <iframe :src="`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&mute=0`" frameborder="0"
                    allowfullscreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"></iframe>
            </div>
        </div>

        <!-- 정보 영역 -->
        <div :class="$style.content" @click="goToVideoDetail">
            <div :class="$style.info">
                <h4 :class="$style.title">{{ video.title }}</h4>
                <p :class="$style.channel">{{ video.channel }}</p>
                <p :class="$style.date">{{ formatDate(video.date) }}</p>
            </div>
            <!-- 찜 버튼 -->
            <div v-if="showLikeButton" :class="$style.likeContainer" @click.stop>
                <button :class="[$style.likeBtn, { [$style.liked]: isLiked }]" @click="handleLikeClick">
                    <img v-if="isLiked" :class="$style.likeHeart" src="@/assets/images/heart.png" alt="찜됨" />
                    <img v-else :class="$style.likeHeart" src="@/assets/images/heart_gray.png" alt="찜하기" />
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ArtistVideoCard",
    props: {
        video: {
            type: Object,
            required: true,
        },
        isLiked: {
            type: Boolean,
            default: false,
        },
        showLikeButton: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["like-toggle"],
    data() {
        return {
            isPlaying: false,
        };
    },
    methods: {
        handleThumbnailClick() {
            this.isPlaying = true;
        },
        goToVideoDetail() {
            this.$router.push({ name: "VideoDetail", params: { id: this.video.id } });
        },
        handleLikeClick() {
            this.$emit("like-toggle", this.video.id);
        },
        formatDate(dateString) {
            if (!dateString) return "";
            try {
                const date = new Date(dateString);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                return `${year}.${month}.${day}`;
            } catch {
                return dateString;
            }
        },
    },
};
</script>

<style module>
/* 기본 카드 (가로형) */
.videoCard {
    display: flex;
    flex-direction: row;
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease, box-shadow 0.2s ease, height 0.3s ease;
    height: 100px;
}

.videoCard:not(.playing):hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* 재생 중 카드 (세로형) */
.playing {
    flex-direction: column;
    height: auto;
}

/* 썸네일 컨테이너 */
.thumbnailContainer {
    width: 140px;
    min-width: 140px;
    height: 100%;
    cursor: pointer;
    position: relative;
    background: #000;
    flex-shrink: 0;
}

.playing .thumbnailContainer {
    width: 100%;
    min-width: unset;
    height: auto;
    aspect-ratio: 16 / 9;
}

.thumbnail {
    width: 100%;
    height: 100%;
    position: relative;
}

.thumbnailImg {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.playOverlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);
    transition: background 0.2s ease;
}

.thumbnailContainer:hover .playOverlay {
    background: rgba(0, 0, 0, 0.5);
}

.playIcon {
    width: 40px;
    height: 40px;
    background: rgba(255, 0, 0, 0.9);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 15px;
    transition: transform 0.2s ease;
}

.thumbnailContainer:hover .playIcon {
    transform: scale(1.1);
}

/* iframe */
.iframeWrapper {
    width: 100%;
    height: 100%;
}

.iframeWrapper iframe {
    width: 100%;
    height: 100%;
    display: block;
}

/* 정보 영역 */
.content {
    flex: 1;
    display: flex;
    align-items: center;
    padding: 12px;
    min-width: 0;
    gap: 10px;
    cursor: pointer;
}

.playing .content {
    flex: unset;
}

.info {
    flex: 1;
    min-width: 0;
}

.title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin: 0 0 6px 0;
    font-family: "Paperlogy6", "NanumSquareRoundB", sans-serif;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.3;
}

.channel {
    font-size: 12px;
    color: #666;
    margin: 0 0 4px 0;
    font-family: "Paperlogy4", "NanumSquareRoundR", sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.date {
    font-size: 11px;
    color: #999;
    margin: 0;
    font-family: "Paperlogy4", "NanumSquareRoundR", sans-serif;
}

/* 찜 버튼 */
.likeContainer {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.likeBtn {
    background: none;
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
}

.likeBtn:hover {
    background: rgba(255, 107, 157, 0.1);
    transform: scale(1.1);
}

.likeBtn.liked {
    animation: heartBeat 0.6s ease-in-out;
}

.likeHeart {
    width: 20px;
    height: 20px;
    transition: all 0.2s ease;
}

@keyframes heartBeat {
    0% {
        transform: scale(1);
    }

    14% {
        transform: scale(1.3);
    }

    28% {
        transform: scale(1);
    }

    42% {
        transform: scale(1.3);
    }

    70% {
        transform: scale(1);
    }
}

/* 모바일 */
@media screen and (max-width: 480px) {
    .videoCard:not(.playing) {
        height: 90px;
    }

    .thumbnailContainer:not(.playing .thumbnailContainer) {
        width: 120px;
        min-width: 120px;
    }

    .playIcon {
        width: 32px;
        height: 32px;
        font-size: 12px;
    }

    .content {
        padding: 10px;
        gap: 8px;
    }

    .title {
        font-size: 13px;
        margin-bottom: 4px;
    }

    .channel {
        font-size: 11px;
        margin-bottom: 2px;
    }

    .date {
        font-size: 10px;
    }

    .likeBtn {
        width: 28px;
        height: 28px;
    }

    .likeHeart {
        width: 16px;
        height: 16px;
    }
}
</style>
